<template>
  <div class="w-full">
    <div
      class="flex my-4"
      :class="{
        'opacity-50 pointer-events-none': loading,
        'flex-col': layout == 'vertical',
        'flex-row': layout == 'horizontal'
      }"
    >
      <div
        v-for="menuItem in menuItems"
        :key="menuItem.key"
        :class="{
          'py-2': layout == 'vertical',
          'px-2 w-full': layout == 'horizontal',
          [menuItem.rootClasses || '']: true
        }"
      >
        <div
          v-if="openedFrom == 'groovesell' && menuItem.key == 'action'"
        ></div>
        <div v-else>
          <div
            v-if="menuItem.heading && openedFrom !== 'groovemail'"
            :class="headingClass"
          >
            {{ menuItem.heading }}
          </div>
          <div
            class="flex flex-row integration-select-config-menu"
            :class="{ 'h-24': menuItem.type == 'TextArea' }"
          >
            <div
              class="flex flex-grow"
              :class="{ 'info-lg py-1 w-6/12': menuItem.inlineLabel }"
            >
              {{ menuItem && menuItem.inlineLabel }}
            </div>
            <component
              v-if="
                (openedFrom == 'groovemail' && menuItem.key !== 'action') ||
                  openedFrom !== 'groovemail'
              "
              :is="menuItem.type"
              v-bind="menuItem"
              :component="component"
              :placeholder="
                menuItem.showPlaceholder === false
                  ? null
                  : menuItem.placeholder || $t('frontend_select')
              "
              :value="values[menuItem.key]"
              :max-height="menuItem.type == 'GdDropdown' ? '16rem' : ''"
              size="full"
              :variant="menuItem.variant || (dark ? 'dark' : 'base')"
              :dark="dark"
              @input="
                setValue({ key: menuItem.key, value: $event, silent: true })
              "
              @change="setValue({ key: menuItem.key, value: $event })"
              @keyup="setValue({ key: menuItem.key, value: $event })"
            ></component>
          </div>
        </div>
      </div>
      <div v-if="menuItems.length && layout == 'horizontal'" class="w-full">
        <div
          class="w-full flex justify-center"
          v-if="openedFrom == 'groovemail'"
        >
          <gd-button
            class="p-4"
            :disabled="!valid || loading"
            :variant="valid && !loading ? 'primary' : 'base'"
            @click="update()"
            >{{ btnLabel }}</gd-button
          >
        </div>
        <div class="mt-7 w-full flex justify-center" v-else>
          <gd-button
            :disabled="!valid || loading"
            :variant="valid && !loading ? 'primary' : 'base'"
            size="lg"
            @click="update()"
            >{{ btnLabel }}</gd-button
          >
        </div>
      </div>
    </div>
    <div
      v-if="menuItems.length && layout == 'vertical'"
      :class="btnBorderClass"
    >
      <div v-if="!loading" class="w-full flex justify-center">
        <gd-button
          :disabled="!valid"
          :variant="valid ? 'primary' : 'base'"
          @click="update()"
          >{{ btnLabel }}</gd-button
        >
      </div>
    </div>
    <iframe ref="loader" class="w-0 h-0"></iframe>
  </div>
</template>
<script>
import api from '@groovepages/gd-lib/api';
import Notify from '../notify';
import { twJoin } from 'tailwind-merge';
import components from './PluginComponents';

export default {
  components,
  props: {
    initialValues: {
      type: Object,
      required: true
    },
    previousContent: {},
    component: {},
    pluginId: {},
    resourcesPath: {},
    serverPath: {},
    plugin: {},
    appData: {},
    updateOnRegister: {},
    layout: { default: 'vertical' },
    dark: { default: true },
    btnLabel: { default: 'Update' },
    showButtonBorders: { default: true },
    showNotificationsOnSave: { default: true },
    openedFrom: { default: 'groovepages' },
    inStylesPanel: { default: false },
    pluginComponent: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      valid: false,
      menuItems: [],
      values: {},
      showMessage: false
    };
  },
  watch: {
    valid(val) {
      this.$emit('changeValidity', val);
    }
  },
  mounted() {
    this.values = { ...this.initialValues };
    this.loadPlugin();
    window.addEventListener('message', this.receiveMessage);
    this.$store.commit('setPluginSettingsModified');
  },
  destroyed() {
    window.removeEventListener('message', this.receiveMessage);
  },
  computed: {
    headingClass() {
      const baseClass = 'info-lg pb-2';
      const variantClass = this.dark
        ? 'font-semibold text-secondary-invert'
        : 'text-grey-700';
      return twJoin(baseClass, variantClass);
    },
    btnBorderClass() {
      const baseClass = 'p-4 w-full';
      const borderClass = this.showButtonBorders ? 'border-t border-b' : '';
      const variantClass = this.dark ? 'border-secondary' : 'border-grey-200';
      return twJoin(baseClass, borderClass, variantClass);
    }
  },
  methods: {
    loadPlugin() {
      const script = document.createElement('script');
      script.src = `${this.resourcesPath + this.plugin}.web.js`;
      this.$refs.loader.contentWindow.document.body.appendChild(script);
    },
    sendRequest(data) {
      this.setMenuLoading(true);
      if (data.method === 'post') {
        api[data.method](data.url, data.data)
          .then(res => {
            this.setMenuLoading(false);
            this.postMessage('callResponse', {
              id: data.messageId,
              data: res
            });
          })
          .catch(() => {
            this.setMenuLoading(false);
          });
      } else {
        api[data.method](data.url)
          .then(result => {
            this.setMenuLoading(false);
            this.postMessage('callResponse', {
              id: data.messageId,
              data: result
            });
          })
          .catch(() => {
            this.setMenuLoading(false);
          });
      }
    },
    postMessage(action, value) {
      return new Promise(resolve => {
        const channel = new MessageChannel();
        channel.port1.onmessage = e => {
          resolve(e.data);
        };
        this.$refs.loader.contentWindow.postMessage(
          {
            type: 'pluginMessage',
            action,
            value
          },
          '*',
          [channel.port2]
        );
      });
    },
    receiveMessage(message) {
      if (message.data.type !== 'pluginMessage') return;
      if (
        !this[message.data.action] &&
        !this.isFunction(this[message.data.action])
      ) {
        message.ports[0].postMessage(
          `Call to undefined method ${message.data.action}`
        );
      } else {
        message.ports[0].postMessage(
          this[message.data.action](message.data.value)
        );
      }
    },
    isFunction(functionToCheck) {
      return (
        functionToCheck &&
        {}.toString.call(functionToCheck) === '[object Function]'
      );
    },
    get_builder_state() {
      return {
        builderMode: this.$store.state.builder.mode,
        isAdmin: this.$store.state.core.user.admin,
        isPopupActive: this.$store.state.specialPages.activeType === 'popup'
      };
    },
    get_api_url() {
      return window.parent.gdEnv.VUE_APP_API_URL;
    },
    get_vue_env() {
      return window.parent.gdEnv.VUE_APP_ENV || 'live';
    },
    getPluginComponentData() {
      if (!this.pluginComponent) return null;
      const { componentType, id, configId, configType } = this.pluginComponent;
      return { componentType, id, configId, configType };
    },
    setValue({ key, value, silent }) {
      this.values[key] = value;
      if (silent) return;
      this.getMenu();
    },
    setValid(state) {
      this.valid = state;
    },
    setMenuLoading(state) {
      this.loading = state;
    },
    update() {
      if (this.showMessage) {
        this.getMenu();
        this.showMessage = false;
      } else {
        this.triggerUpdate();
      }
    },
    toggleShowMessage(state) {
      this.showMessage = state;
    },
    showNotification(data) {
      if (this.showNotificationsOnSave) {
        if (data.success) {
          Notify.success(data.message);
        }
        if (data.error) {
          Notify.fail(data.message);
        }
      }
    },
    getPluginBtnLbls({ selector }) {
      const selectors = selector.split(',');
      const previousContentDOM = document.createElement('div');
      previousContentDOM.innerHTML = this.previousContent;
      let button = null;
      let count = 0;
      try {
        do {
          button = previousContentDOM.querySelector(selectors[count]);
          count += 1;
        } while (!button && count < selectors.length);
        if (button && !button.children.length) return button.textContent.trim();
        if (button) return button.childNodes[0].textContent.trim();
        return false;
      } catch (err) {
        console.log(err);
        return false;
      } finally {
        previousContentDOM.remove();
      }
    },
    getPluginBtnSubLblsCount({ selector }) {
      const previousContentDOM = document.createElement('div');
      previousContentDOM.innerHTML = this.previousContent;
      try {
        const subLabels = previousContentDOM.querySelectorAll(selector);
        if (subLabels) return subLabels.length;
        return false;
      } catch (err) {
        console.log(err);
        return false;
      } finally {
        previousContentDOM.remove();
      }
    },
    getInputAttr({ selector, attr, getText }) {
      const previousContentDOM = document.createElement('div');
      previousContentDOM.innerHTML = this.previousContent;
      try {
        const input = previousContentDOM.querySelector(selector);
        if (input)
          return getText ? input.textContent.trim() : input.getAttribute(attr);
        return false;
      } catch (err) {
        console.log(err);
        return false;
      } finally {
        previousContentDOM.remove();
      }
    },
    updateMenu(menuItems) {
      this.menuItems = menuItems;
      this.values.info = undefined;
      this.menuItems.forEach(item => {
        if (
          typeof this.values[item.key] === 'undefined' ||
          item.key === 'info'
        ) {
          if (item.defaultFallbackFn && !this.values[item.key]) {
            const params = item.defaultFallbackFn.params || null;
            this.values[item.key] =
              this[item.defaultFallbackFn.name](params) || item.default;
          } else {
            this.values[item.key] = item.default;
          }
        }
      });

      this.$emit('menu-updated', { menuItems: this.menuItems });
    },
    triggerUpdate() {
      if (this.previousContent) {
        this.values.previousContent = this.previousContent;
      }
      this.postMessage('update', this.values).then(value => {
        this.$emit('update', { config: this.values, value });
      });
    },
    getMenu() {
      this.postMessage('menu', this.values).then(menuItems => {
        this.updateMenu(menuItems);
      });
    },
    serverCall(data) {
      this.setMenuLoading(true);
      api
        .post(data.url, data.input)
        .then(res => {
          this.setMenuLoading(false);
          if (data.input.action === 'set-key') {
            this.postMessage('menu', {
              showMessage: true,
              message: res.message
            });
            this.showMessage = true;
          }
          this.postMessage('callResponse', {
            id: data.messageId,
            data: res
          });
        })
        .catch(() => {
          this.setMenuLoading(false);
        });
    },
    register() {
      setTimeout(() => {
        if (this.updateOnRegister) this.triggerUpdate();
        this.getMenu();
      });
      let baseURL = 'https://app.groovefunnels.com';
      if (window?.parent?.gdEnv?.VUE_APP_FRONTEND_NETWORK) {
        baseURL = window.parent.gdEnv.VUE_APP_FRONTEND_NETWORK.replace(
          /\/groovepages.*$/gi,
          ''
        );
      }
      return {
        appData: this.appData,
        id: this.pluginId,
        resourcesPath: this.resourcesPath,
        serverPath: this.serverPath,
        baseURL,
        locale: window.localStorage.getItem('lang')
      };
    }
  }
};
</script>
<style>
.-mt-19 {
  margin-top: -5.3rem;
}
</style>
